import React, { useState } from 'react';
import { Box } from '@mui/material';
import useMediaQuery from '@mui/system/useMediaQuery';
import colors from '../../theme/colors';
import { breakpoints } from '../../theme';
import * as T from '../Typography';
import Button from '../Button';
import feedbackReportReadySvg from '../assets/feedback-report-ready.svg';
import { SurveyCardWrapper, IconWrapper, ButtonGroup } from './style';
import { routes } from '../../constants';
import { handleFileDownload } from '../../helpers/handle-file-download';
import { useAuth } from '../../context/auth';
import type { SurveyData } from '../../api-calls/survey.queries';

interface SectoralReportsCardProps {
  status: SurveyData['status'];
}

const SectoralReportsCard: React.FC<SectoralReportsCardProps> = ({ status }) => {
  const { user, token } = useAuth();
  const [isDownloading, setIsDownloading] = useState(false);
  const isWideScreen = useMediaQuery(`(min-width:${breakpoints.tablet}px)`);
  const { VITE_API_URL } = import.meta.env;

  const sectoralReportsDownloadUrl = `${VITE_API_URL}/survey/${user?.salesforceContactId}/download-report?type=sectoral`;

  const content = {
    title: 'Your Sectoral Reports are ready!',
    description: (
      <>
        Access detailed sectoral insights and analysis. Dive in to explore the
        results and learn from the data.
      </>
    ),
    buttons: [
      {
        label: 'Download',
        action: () => handleFileDownload(sectoralReportsDownloadUrl, '', token, setIsDownloading),
        isLoading: isDownloading
      },
      {
        label: 'View full Report', action: () => {
          const url = routes.VIEW_REPORT.replace(':salesforceContactId', user?.salesforceContactId || '');
          const urlWithToken = `${url}?token=${encodeURIComponent(token || '')}&type=sectoral`;
          window.open(urlWithToken, '_blank');
        }
      }
    ],
    imgSrc: feedbackReportReadySvg,
    cardBackground: colors.primary.shades.shade50
  };

  if (status !== 'report_ready') {
    return null;
  }

  return (
    <SurveyCardWrapper
      flexDirection={isWideScreen ? 'row' : 'column'}
      cardBackground={content.cardBackground}
      data-testid="survey-card"
    >
      <IconWrapper>
        <img src={content.imgSrc} alt="Survey Icon" />
      </IconWrapper>
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        mb={isWideScreen ? 0 : 3}
      >
        <T.TextMediumBold
          color={colors.neutral.shades.shade700}
          ta={isWideScreen ? 'left' : 'center'}
        >
          {content.title}
        </T.TextMediumBold>
        <T.TextSmallRegular
          mt={2}
          color={colors.neutral.shades.shade600}
          ta={isWideScreen ? 'left' : 'center'}
        >
          {content.description}
        </T.TextSmallRegular>
      </Box>
      <ButtonGroup
        flexDirection={isWideScreen ? 'row' : 'column'}
        alignItems={isWideScreen ? 'flex-end' : 'center'}
      >
        {content.buttons.map((button, index) => (
          <Button
            key={index}
            handleClick={(e: any) => {
              e.preventDefault();
              e.stopPropagation();
              button.action();
            }}
            variant={index === 0 ? 'primary' : 'blue'}
            width={isWideScreen ? 'fit-content' : '100%'}
            loading={button.isLoading}
            disabled={false}
            to=""
            external={false}
          >
            {button.label}
          </Button>
        ))}
      </ButtonGroup>
    </SurveyCardWrapper>
  );
};

export default SectoralReportsCard;
